import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Vision.css';

const HERO_IMG = '/mascot.png';

const PILLARS = [
  {
    id: '01',
    title: 'Government Efficiency & Accountability',
    points: [
      'Conduct a strategic study for a long-term village vision',
      'Create performance metrics through a village report card',
      'Survey residents on spending priorities',
      'Explore insurance recovery options for emergency services',
    ],
  },
  {
    id: '02',
    title: 'Government Partnerships',
    points: [
      'Strengthen county-level coordination',
      'Expand collaboration with the Town of Horseheads on shared services',
      'Recognize that village residents also pay town taxes — maximize value',
    ],
  },
  {
    id: '03',
    title: 'Hanover Square Events',
    points: [
      'Increase community gatherings at Hanover Square',
      'Build partnerships between recreation department, local merchants, and volunteers',
      'Create regular programming that brings the community together',
    ],
  },
  {
    id: '04',
    title: 'Mayoral & Trustee Accountability',
    points: [
      'Establish semiannual trustee board retreats for planning and evaluation',
      'Host regular public forums for resident engagement',
      'Transparent decision-making with community input',
    ],
  },
  {
    id: '05',
    title: 'Infrastructure Improvements',
    points: [
      'Accelerate the water system rebuild',
      'Conduct a comprehensive water audit',
      'Implement leaf collection services',
      'Increase investment in parks and public spaces',
    ],
  },
  {
    id: '06',
    title: 'Digital & Employee Focus',
    points: [
      'Upgrade to a .gov website for credibility and security',
      'Establish official social media accounts',
      'Conduct workforce benchmarking studies',
      'Reinstate performance evaluations for accountability',
    ],
  },
];

function Vision() {
  const pageRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    const els = pageRef.current?.querySelectorAll('.animate');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="vision-page" ref={pageRef}>
      {/* HERO */}
      <section className="vision-hero">
        <div className="vision-hero__inner">
          <div className="vision-hero__text">
            <span className="label animate">The Platform</span>
            <h1 className="animate">A vision for Horseheads</h1>
            <p className="animate">
              Six priorities built on listening to residents, strategic planning,
              and a commitment to accelerating our community forward.
            </p>
          </div>
          <div className="vision-hero__img animate">
            <img src={HERO_IMG} alt="Horseheads community" />
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="pillars">
        <div className="pillars__inner">
          {PILLARS.map((pillar) => (
            <div className="pillar animate" key={pillar.id}>
              <div className="pillar__header">
                <span className="pillar__num">{pillar.id}</span>
                <h2>{pillar.title}</h2>
              </div>
              <ul className="pillar__list">
                {pillar.points.map((point, i) => (
                  <li key={i}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="vision-cta">
        <div className="vision-cta__inner animate">
          <h2>Have ideas for Horseheads?</h2>
          <p>Kevin wants to hear your priorities. Every voice shapes the plan.</p>
          <Link to="/contact" className="btn btn--primary">Share Your Thoughts</Link>
        </div>
      </section>
    </div>
  );
}

export default Vision;
